import { Button, cn } from '@bridebook/aura';
import { Pencil } from '@bridebook/aura/src/icons';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import {
  TDetailsSections,
  useEditorSyncContext,
} from '@bridebook/web/components/wedding-website/main/desktop/editor-sync-context';
import { useReorderMode } from '@bridebook/web/components/wedding-website/wedding-details/reorder-mode/reorder-mode-provider';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { interactedWithWeddingWebsiteAnalyticsButtonClick } from '@bridebook/web/lib/wedding-website/analytics';
import { useCallback } from 'react';
import {
  useIsEditMode,
  useIsSidebarEditor,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-provider-panel';

type Props = {
  className?: string;
  /** Clicking the edit button will trigger a details sync for this section ID (link on mobile, scroll to section and open on desktop) */
  detailsSectionId: TDetailsSections;
  /** Skip the hover-reveal styling — for callers that already control when the button appears. */
  alwaysVisible?: boolean;
};

export const FloatingEditButton = ({ className, detailsSectionId, alwaysVisible }: Props) => {
  const dispatch = useDispatch();
  const editMode = useIsEditMode();
  const isSidebarEditor = useIsSidebarEditor();
  const { triggerDetailsSync } = useEditorSyncContext();
  const { setReorderMode } = useReorderMode();

  const handleEditClick = useCallback(() => {
    // If we are in sidebar editor mode (editing mode always true here), trigger the details sync to open the correct section
    if (isSidebarEditor) {
      setReorderMode(false);
      triggerDetailsSync(detailsSectionId);
    }

    // Analytics should trigger regardless of view mode
    dispatch(
      interactedWithWeddingWebsiteAnalyticsButtonClick({
        section: 'weddingLocation',
        element: 'editButton',
      }),
    );
  }, [dispatch, triggerDetailsSync, detailsSectionId, isSidebarEditor, setReorderMode]);

  // Link is only needed when not in sidebar editor mode to navigate to the correct section (mobile)
  const link =
    !isSidebarEditor && detailsSectionId
      ? UrlHelper.website.weddingDetails.sectionAnchor(detailsSectionId)
      : null;

  // If not in edit mode, do not render the button at all
  if (!editMode) return null;

  return (
    <div
      className={cn(
        'absolute right-4 bottom-4 z-[10] @screen-md:right-6 @screen-md:bottom-6',
        // Only hide and show on hover for devices with fine pointer (mouse)
        // On coarse pointer devices (touch), buttons stay visible
        !alwaysVisible &&
          'opacity-100 [@media(pointer:fine)]:opacity-0 [@media(pointer:fine)]:transition-all [@media(pointer:fine)]:duration-150 [@media(pointer:fine)]:ease-in-out [@media(pointer:fine)]:group-hover:opacity-100',
        className,
      )}>
      <Button
        icon={Pencil}
        size={!isSidebarEditor ? 'sm' : 'standard'}
        className={cn(
          isSidebarEditor && 'scale-85 @screen-md:scale-100',
          // Force icon-only size when rendered as link to keep circular shape
          link && (!isSidebarEditor ? 'h-[32px] w-[32px]' : 'h-[40px] w-[40px]'),
        )}
        variant="secondary"
        asChild={!!link}
        onClick={handleEditClick}>
        {!!link && <Link href={link} />}
      </Button>
    </div>
  );
};
